import { CheckCircle2 } from "lucide-react";

import { cn, focusRing } from "../../components/ui";
import { getOpenWorkSeverity, getOpenWorkSeverityLabel } from "./openWorkSeverity";

const TONE_STYLES = {
  few: "border-warning bg-warning-subtle text-warning",
  many: "border-danger bg-danger-subtle text-danger",
};

function QueueCard({
  queue,
  count,
  active,
  loading,
  onSelect,
  idPrefix,
  panelId,
  countNoun,
}) {
  const severity = loading ? null : getOpenWorkSeverity(count);
  const isClear = severity === "clear";
  const Icon = isClear ? CheckCircle2 : queue.icon;
  const severityLabel = loading ? "Loading count" : getOpenWorkSeverityLabel(count);

  return (
    <button
      id={`${idPrefix}-${queue.id}`}
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={panelId}
      disabled={loading}
      onClick={() => onSelect(queue.id)}
      className={cn(
        "group min-w-64 snap-start rounded-panel border p-4 text-left transition-colors duration-150",
        "disabled:cursor-wait disabled:opacity-70 md:min-w-0",
        isClear
          ? "border-default bg-surface text-secondary hover:border-strong"
          : TONE_STYLES[severity],
        active && "border-accent ring-1 ring-accent",
        focusRing,
      )}
    >
      <span className="flex items-start justify-between gap-4">
        <span className="min-w-0">
          <span className="flex items-center gap-2 text-panel-title">
            <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
            {queue.title}
          </span>
          {queue.description ? (
            <span className="mt-2 block text-body-sm opacity-90">{queue.description}</span>
          ) : null}
        </span>
        <span
          className="shrink-0 text-metric tabular-nums"
          aria-label={loading ? severityLabel : `${count} ${countNoun}, ${severityLabel.toLocaleLowerCase()}`}
        >
          {loading ? "—" : count}
        </span>
      </span>
      <span className="mt-4 flex min-h-5 items-center justify-between gap-3 text-control-sm">
        <span>{isClear ? "Queue clear" : queue.actionLabel}</span>
        {active ? <span className="text-meta font-medium">Selected</span> : null}
      </span>
    </button>
  );
}

export default function OpenWorkQueueCards({
  queues,
  active,
  loading = false,
  onChange,
  idPrefix,
  panelId,
  label,
  countNoun = "items",
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        "flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-2 md:overflow-visible md:pb-0",
        queues.length > 4 ? "xl:grid-cols-5" : "xl:grid-cols-4",
      )}
    >
      {queues.map((queue) => (
        <QueueCard
          key={queue.id}
          queue={queue}
          count={queue.count}
          active={active === queue.id}
          loading={loading}
          onSelect={onChange}
          idPrefix={idPrefix}
          panelId={panelId}
          countNoun={countNoun}
        />
      ))}
    </div>
  );
}
